import { Modal, Progress } from 'antd';
import { FileExcelOutlined } from '@ant-design/icons';
import uploadIcon from '../../../assets/icons/upload-icon.png';
import React, { FC, useRef, useState } from 'react';
import ButtonComponent from '../ButtonComponent';
import Notification from '../Notification';
import { NotificationTypes } from '../../../enums/notificationTypes';
import { KtExcelUploadService, KtImportProgress } from '../../../services/AgreementService/ktExcelUpload.service';
import './uploadKTModal.scss';

interface UploadKTModalProps {
    visible: boolean;
    agreementId: string;
    onClose: () => void;
    onSuccess: () => void;
}

const ALLOWED_EXTENSIONS = ['.xlsx', '.xls'];

const UploadKTModal: FC<UploadKTModalProps> = ({ visible, agreementId, onClose, onSuccess }) => {
    const [file, setFile] = useState<File | null>(null);
    const [isDragging, setIsDragging] = useState(false);
    const [progress, setProgress] = useState(0);
    const [processed, setProcessed] = useState(0);
    const [total, setTotal] = useState(0);
    const [submitting, setSubmitting] = useState(false);
    const [importing, setImporting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const eventSourceRef = useRef<EventSource | null>(null);

    const isValidFile = (f: File) => {
        const ext = f.name.substring(f.name.lastIndexOf('.')).toLowerCase();
        return ALLOWED_EXTENSIONS.includes(ext);
    };

    const handleFileSelect = (f: File) => {
        if (!isValidFile(f)) {
            setError('Only .xlsx and .xls files are allowed');
            return;
        }
        setFile(f);
        setError(null);
    };

    const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        setIsDragging(false);
        const dropped = e.dataTransfer.files[0];
        if (dropped) handleFileSelect(dropped);
    };

    const handleClose = () => {
        if (eventSourceRef.current) {
            eventSourceRef.current.close();
            eventSourceRef.current = null;
        }
        setFile(null);
        setProgress(0);
        setProcessed(0);
        setTotal(0);
        setError(null);
        setSubmitting(false);
        setImporting(false);
        onClose();
    };

    const handleSubmit = async () => {
        if (!file) return;
        setSubmitting(true);
        setError(null);

        try {
            const filename = await KtExcelUploadService.uploadFile(agreementId, file);
            await KtExcelUploadService.validateStructure(agreementId, filename);
            await KtExcelUploadService.validateData(agreementId, filename);
            await KtExcelUploadService.validateKt(agreementId, filename);

            setImporting(true);
            setSubmitting(false);

            eventSourceRef.current = KtExcelUploadService.importStream(
                agreementId,
                filename,
                (event: KtImportProgress) => {
                    setProgress(event.progress || 0);
                    setProcessed(event.processed || 0);
                    setTotal(event.total || 0);
                },
                (event: KtImportProgress) => {
                    setProgress(100);
                    Notification({
                        message: 'Key Terms',
                        description: `Import complete — ${event.created_count} created, ${event.updated_count} updated`,
                        type: NotificationTypes.SUCCESS as string,
                    });
                    onSuccess();
                    handleClose();
                },
                (errorMsg: string) => {
                    setImporting(false);
                    setError(errorMsg);
                }
            );
        } catch (err) {
            const msg = (err as any)?.response?.data?.error || 'An error occurred. Please try again.';
            setError(msg);
            setSubmitting(false);
        }
    };

    return (
        <Modal
            visible={visible}
            title="Upload KT"
            onCancel={!importing ? handleClose : undefined}
            closable={!importing}
            maskClosable={false}
            centered
            className="upload-kt__modal"
            maskStyle={{ background: 'rgba(43, 43, 43, 0.9)' }}
            footer={
                <div className="upload-kt__modal--footer">
                    <ButtonComponent type="ghost" onClick={handleClose}>
                        Cancel
                    </ButtonComponent>
                    <ButtonComponent
                        type="primary"
                        onClick={handleSubmit}
                        disabled={!file || importing}
                        loading={submitting}
                    >
                        Submit
                    </ButtonComponent>
                </div>
            }
        >
            <div className="upload-kt__modal--body">
                {importing && (
                    <div className="upload-kt__modal--progress-top">
                        <Progress
                            type="circle"
                            percent={progress}
                            width={73}
                            strokeWidth={10}
                            strokeColor="#00629B"
                            status={progress === 100 ? 'success' : 'normal'}
                        />
                        <p className="upload-kt__modal--progress-text">
                            {processed > 0
                                ? `Processing row ${processed} of ${total}...`
                                : 'Starting import...'}
                        </p>
                    </div>
                )}
                <div
                    className={`upload-kt__modal--dropzone ${isDragging ? 'dragging' : ''} ${importing ? 'disabled' : ''}`}
                    onClick={() => !importing && fileInputRef.current?.click()}
                    onDragOver={(e) => { if (!importing) { e.preventDefault(); setIsDragging(true); } }}
                    onDragLeave={() => setIsDragging(false)}
                    onDrop={(e) => { if (!importing) handleDrop(e); }}
                >
                    <img src={uploadIcon} alt="upload" className="upload-kt__modal--dropzone-icon" />
                    <p className="upload-kt__modal--dropzone-text">
                        Drag &amp; Drop / Click here to Upload <span className="upload-kt__modal--dropzone-text-bold">.xlsx</span>
                    </p>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept=".xlsx,.xls"
                        style={{ display: 'none' }}
                        onChange={(e) => {
                            const f = e.target.files?.[0];
                            if (f) handleFileSelect(f);
                            e.target.value = '';
                        }}
                    />
                </div>
                {file && (
                    <div className="upload-kt__modal--file-info">
                        <FileExcelOutlined className="upload-kt__modal--file-icon" />
                        <span>{file.name}</span>
                    </div>
                )}
                {error && (
                    <div className="upload-kt__modal--error-list">
                        {error.split(/\. (?=Row )/).map((msg, i) => (
                            <p key={i} className="upload-kt__modal--error-item">• {msg.replace(/\.$/, '')}</p>
                        ))}
                    </div>
                )}
            </div>
        </Modal>
    );
};

export default UploadKTModal;
